import { camelToSnake, snakeToCamel } from './string'

/**
 * Clones the given object using JSON conversions.
 *
 * @param {Object} object
 * @returns {Object}
 */
export const clone = function(object) {
  return JSON.parse(JSON.stringify(object))
}

/**
 * Returns true if the given property is present in the given object.
 * Utilises the raw object prototype function because `hasOwnProperty` can be overwritten in the original object.
 *
 * @param {Object} object
 * @param {string} property
 * @returns {boolean}
 */
export const hasProperty = function(object, property) {
  return Object.prototype.hasOwnProperty.call(object, property)
}

/**
 * Returns true if the given object is either empty or doesn't exist at all.
 *
 * @param {Object|undefined} object
 * @return {boolean}
 */
export const isEmptyObject = function(object) {
  return !object || Object.keys(object).length === 0
}

/**
 * Safely search an object for a property using a variable amount of property keys.
 * Will check that all needed parts of the object exists, and return undefined if not.
 *
 * @param {Object|undefined} object The element for which to search for a property.
 * @param {string} key Variable amount of property keys to look for in the given object.
 * @param {*=} defaultValue value to return if nothing was found.
 * @return {*} The requested property, or undefined if it doesn't exist.
 */
export const getProperty = function(object, key, defaultValue) {
  if (!object) {
    return defaultValue
  }

  const keys = key.split('.')
  const currentKey = keys.shift()

  let value = undefined

  if (!hasProperty(object, currentKey)) {
    return defaultValue
  } else {
    value = object[currentKey]
  }

  if (keys.length > 0) {
    if (value) {
      return getProperty(value, keys.join('.'), defaultValue)
    } else {
      return defaultValue
    }
  }

  return value
}

/**
 * Returns true if the given value is an object.
 *
 * @param {*} value
 * @return {boolean}
 */
export const isObject = value => {
  return !!value && value.constructor === Object
}

/**
 * Returns true if the given value is an array.
 *
 * @param {*} value
 * @return {boolean}
 */
export const isArray = value => {
  return !!value && value.constructor === Array
}

/**
 * Returns true if the given value is an object or an array.
 *
 * @param {*} value
 * @return {boolean}
 */
export const isObjectOrArray = value => {
  return (
    !!value && (value.constructor === Object || value.constructor === Array)
  )
}

/**
 * Returns a new object (or array, depending on input type) with all keys transformed from camelCase to snake_case.
 * If `nested` is set to true all contained objects will have their keys transformed as well.
 *
 * Returns any given falsy input as is.
 *
 * @param {Array|Object} input
 * @param {boolean=} nested
 * @return {Array|Object|undefined}
 */
export const camelToSnakeKeys = (input, nested = false) => {
  return transformKeys(input, camelToSnake, nested)
}

/**
 * Returns a new object (or array, depending on input type) with all keys transformed from snake_case to camelCase.
 * If `nested` is set to true all contained objects will have their keys transformed as well.
 *
 * Returns any given falsy input as is.
 *
 * @param {Array|Object} input
 * @param {boolean=} nested
 * @return {Array|Object|undefined}
 */
export const snakeToCamelKeys = (input, nested = false) => {
  return transformKeys(input, snakeToCamel, nested)
}

/**
 * Returns a new object (or array, depending on input type) with all keys transformed with the given transformer.
 * If `nested` is set to true all contained objects will have their keys transformed as well.
 *
 * Returns any given falsy input as is.
 *
 * @param {Array|Object} input
 * @param {function(string):string} transformer
 * @param {boolean=} nested
 * @return {Array|Object|undefined}
 */
export const transformKeys = (input, transformer, nested = false) => {
  if (!input) {
    return input
  }

  if (isArray(input)) {
    return input.map(value => {
      if (nested && isObjectOrArray(value)) {
        return transformKeys(value, transformer, true)
      }

      return value
    })
  }

  const newObject = {}

  Object.entries(input).forEach(([key, value]) => {
    if (nested && isObjectOrArray(value)) {
      value = transformKeys(value, transformer, true)
    }

    newObject[transformer(key)] = value
  })

  return newObject
}

export const objectToFormData = object => {
  const formData = new FormData()

  Object.keys(object).forEach(key => {
    formData.append(key, object[key])
  })

  return formData
}
